/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */
import React from "react";
import { useSelector } from "react-redux";
import { setScheduledTask } from "../../../store/api/tasks/reducer";
import { tasksSelector } from "../../../store/api/tasks/selector";
import { setErrorMsg } from "../../../store/helpers/helperReducers";
import { errorMsgSelector } from "../../../store/helpers/helperSelectors";
import { useStoreDispatch } from "../../../store/hooks";
import { NODE_LCI_PASSWARD_MAXLENGTH } from "../../../globals/globals";
import { InputField } from "../../Shared/Radix/Inputs/InputField";
import { PasswordField } from "../../Shared/Radix/Inputs/PasswordField";

export const SetLCIPassword = () => {
    const dispatch = useStoreDispatch();
    const errorMsg = useSelector(errorMsgSelector);
    const scheduledtasks = useSelector(tasksSelector);
    const onPasswordChange = (name: string, value: string) => {
        dispatch(setScheduledTask({ [name]: value }));
        if (name === "password") {
            if (!value) {
                dispatch(setErrorMsg({ ...errorMsg, [name]: "Password is required!" }));
            } else {
                const regex = new RegExp("^[A-Za-z0-9@_':%!+=,?$#\/-]+$");
                if (!regex.test(value)) {
                    dispatch(setErrorMsg({ ...errorMsg, [name]: "Invalid input. Only allowed special characters are @_':%!+=/-,?$#" }));
                } else {
                    dispatch(setErrorMsg({ ...errorMsg, [name]: "" }));
                }
            }
            // Also revalidate confirm if it has a value
            if (scheduledtasks?.confirmPassword) {
                if (value !== scheduledtasks.confirmPassword) {
                    dispatch(setErrorMsg({ ...errorMsg, [name]: "", confirmPassword: "Passwords do not match" }));
                } else {
                    dispatch(setErrorMsg({ ...errorMsg, [name]: "", confirmPassword: "" }));
                }
            }
        }
    };

    const onConfirmPasswordChange = (name: string, value: string) => {
        dispatch(setScheduledTask({ [name]: value }));
        if (scheduledtasks?.password !== value) {
            dispatch(setErrorMsg({ ...errorMsg, [name]: "Passwords do not match" }));
        } else {
            dispatch(setErrorMsg({ ...errorMsg, [name]: "" }));
        }
    };

    const onBlurHandler = (e: React.FocusEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        if (name === "password") {
            if (!value) {
                dispatch(setErrorMsg({ ...errorMsg, [name]: "Password is required!" }));
            } else {
                const regex = new RegExp("^[A-Za-z0-9@_':%!+=,?$#\/-]+$");
                if (!regex.test(value)) {
                    dispatch(setErrorMsg({ ...errorMsg, [name]: "Invalid input. Only allowed special characters are @_':%!+=/-,?$#" }));
                }
            }
        }
        if (name === "confirmPassword") {
            if (!value) {
                dispatch(setErrorMsg({ ...errorMsg, [name]: "Confirm password is required!" }));
            } else if (scheduledtasks?.password !== value) {
                dispatch(setErrorMsg({ ...errorMsg, [name]: "Passwords do not match" }));
            } else {
                dispatch(setErrorMsg({ ...errorMsg, [name]: "" }));
            }
        }
    };

    const onConfirmBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        if (scheduledtasks?.password !== value) {
            dispatch(setErrorMsg({ ...errorMsg, [name]: "Passwords do not match" }));
        } else {
            dispatch(setErrorMsg({ ...errorMsg, [name]: "" }));
        }
    };

    return (
        <div className="flex flex-col gap-6 py-5">
            <p className="text-sm font-medium">Set the node LCI Password</p>
            <PasswordField
                name="password"
                label="LCI Password"
                value={scheduledtasks?.password || ""}
                required
                placeholder="Enter LCI password"
                maxLength={NODE_LCI_PASSWARD_MAXLENGTH}
                onChange={onPasswordChange}
                onBlur={onBlurHandler}
            />
            <InputField
                label="Confirm Password"
                name="confirmPassword"
                value={scheduledtasks?.confirmPassword ?? ""}
                type="password"
                required
                onChange={onConfirmPasswordChange}
                onBlur={onBlurHandler}
                placeholder="Enter confirm password"
            />
        </div>
    );
};